import {useEffect, useRef, useState} from 'react';
import styles from './discountInfo.module.scss';
import { ReactComponent as ArrowSVG } from 'assets/icon-arrow-right.svg';
import discountInfo from "assets/discountInfo.jpg"; 
import {getCouponDetail} from "../../api/query";
import {useAuth} from "../../context/Auth";
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import Dialog from 'components/Dialog';
import Barcode from 'components/Barcode';
import { resolveImageUrl } from 'utils/imageUrl';
import { useGlobalError } from 'context/Error/GlobalErrorContext';


const CouponDetail = () => {
  const [doubleCheckVisible, setDoubleCheckVisible] = useState<boolean>(false);
  const { accessToken } = useAuth();
  const { pathname } = useLocation();
  const { couponId } = useParams();
  const [searchParams] = useSearchParams();
  const claimedAt = searchParams.get('claimed_at');
  const [coupon,setCoupon] = useState<any>(undefined);
  // 讀取中/讀不到/找不到要分開講,不然還沒回來就先秀「尚未配發」,讀取失敗也會被說成找不到
  const [loadState, setLoadState] = useState<'loading' | 'done' | 'error'>('loading');
  const [codeCopy,setCopy] = useState(false);
  const promotionCode = useRef<string>('');
  const promotionUrl = useRef<string>('');
  const [expiredButton,setExpiredButton] = useState(false);
  const { showError } = useGlobalError()

  const now = new Date();

  useEffect(()=>{
    if(pathname){
      fetchCoupom();
    }
    // eslint-disable-next-line
  },[pathname]);
  
  const fetchCoupom = async() =>  {
    setLoadState('loading');
    try{
      const { data } = await getCouponDetail(accessToken! , Number(couponId!));
      if(!data){
        return 'coupon not found';
      }else{
        // getCouponDetail 回的是「這位會員已領的券清單」,不是單一張券。
        // 原本寫成 data.data.data 又多剝一層,拿到 undefined,一讀 expired_at 就整頁白屏
        // (同仁回報「所有優惠券都是」)。這裡改成從清單挑出網址上的那張。
        const list: any[] = Array.isArray(data.data)
          ? data.data
          : Array.isArray((data.data as any)?.data)
            ? (data.data as any).data
            : [];
        // 同一張券領過多次時(一碼一券,每次一組序號)用領取時間對到是哪一次
        const sameCoupon = list.filter((c) => String(c?.id) === String(couponId));
        const couponData =
          (claimedAt && sameCoupon.find((c) => c?.created_at === claimedAt)) ||
          sameCoupon[0];

        if (couponData) {
          setCoupon(couponData);
          promotionCode.current = couponData.promotion_code ?? '';
          promotionUrl.current = couponData.promotion_url ?? '';

          // 沒有到期日就不當作過期(後端可能回空字串)
          const expiredDate = couponData.expired_at ? new Date(couponData.expired_at) : null;
          setExpiredButton(
            expiredDate !== null &&
              !Number.isNaN(expiredDate.getTime()) &&
              expiredDate.getTime() < now.getTime()
          );
        } else {
          setCoupon(undefined);
        }
      }
      setLoadState('done');
    }catch(e: any){
      console.log(e);
      setLoadState('error');
      showError(e, 'error')
    }
  }
  
  const onCopy = () => {
    if (!navigator.clipboard) {
      return; // Clipboard API not available
    }
    
    if(promotionCode.current){
      navigator.clipboard
        .writeText(promotionCode.current)
        .then(() => {
          setCopy(true);
        })
        .catch((err) => {
          console.log('Something went wrong', err);
        });  
      }
  };

  const showDoubleCheckConfirm = () => {
    setDoubleCheckVisible(true);
  }

  const onCancel= () => {
    setDoubleCheckVisible(false);
  };

  const onCancelButtonClick = () => {
    setDoubleCheckVisible(false);
  };

  const onDoubleCheckConfirm = () => {
    if (coupon?.promotion_url) {
      if (coupon?.promotion_url.includes('http')) {
        window.open(coupon?.promotion_url); 
      }
    }
    setDoubleCheckVisible(false);
  };
  
  const hasUrl = !!coupon?.promotion_url;
  // 一碼一券的兌換序號(全家咖啡券這類)要給店員用 POS 刷,出條碼;
  // 有連結的是電商折扣碼,貼到對方網站用,不需要條碼
  const showBarcode = !!coupon?.promotion_code && coupon?.use_type === 1 && !hasUrl;
  // 沒碼也沒連結時不能再叫人「點擊下方按鈕」—— 下面根本沒有按鈕(10/7 Yuka 回報)
  const noCodeText =
    loadState === 'loading' ? '載入中…'
    : loadState === 'error' ? '讀取失敗，請稍後再試'
    : !coupon ? '找不到這張券'
    : hasUrl ? '點擊下方按鈕，前往兌換'
    : coupon.use_type === 3 ? '此券為直接折抵，無需兌換碼'
    : '兌換碼尚未配發，請聯繫客服';
  const codeHint = !coupon?.promotion_code
    ? ''
    : showBarcode
      ? '請出示條碼給店員掃描'
      : `${codeCopy ? '已成功複製代碼' : '複製代碼'}${hasUrl ? '，點擊下方按鈕，前往兌換' : ''}`;
  // 新後端已領券沒有回開始日,只有到期日;沒有開始日就不要印出前面空一塊的「 ~ 」
  const startDate = coupon?.start_at ? coupon.start_at.slice(0, 10) : '';
  const endDate = coupon?.expired_at ? coupon.expired_at.slice(0, 10) : '';
  const dateRange = startDate ? `${startDate} ~ ${endDate}` : endDate ? `至 ${endDate}` : '無期限';

  return (
    <>
      <div className={styles.discountInfo}>
        <div className={styles.imgWrap}>
          <div className={styles.imgInner}>
            {/* 上方大圖用活動主視覺;券圖是列表的方形縮圖,放這裡比例不對 */}
            <img
              src={resolveImageUrl(coupon?.banner_url) || discountInfo}
              onError={(e) => {
                if (!e.currentTarget.src.endsWith(discountInfo)) e.currentTarget.src = discountInfo;
              }}
              alt="couponImage"
              className={styles.img}
            />
          </div>
        </div>
        <div className={styles.redeemCode}>
          <div className={styles.code}>
            { !expiredButton ?
              (
                <>
                  {showBarcode && (
                    <div className={styles.barcodeBox}>
                      <Barcode value={coupon.promotion_code} className={styles.barcode} />
                    </div>
                  )}
                  <h3>{ coupon?.promotion_code ? coupon?.promotion_code : noCodeText }</h3>
                  {coupon?.pin_code && <h3>密碼：{coupon.pin_code}</h3>}
                  <span>{codeHint}</span>
                </>
              )
              :
              (
                <h3>兌換券已超過兌換期限</h3>
              )
            }
              {/* <span>{codeCopy?'已成功複製代碼，點擊下方按鈕，前往兌換':'複製代碼，點擊下方按鈕，前往兌換'}</span> */}
          </div>
          {
            (coupon?.promotion_code && !expiredButton && !showBarcode) && (
              <div className={styles.copyBtn}>
                <button onClick={onCopy}>
                  {/* {typeRef.current === 'online' ? '複製代碼' : '確認兌換'} */}
                  複製代碼
                </button>
              </div>
            )
          }
        </div>
        <div className={styles.extra}>
          <div className={styles.dates}>
          兌換期限：{dateRange}
          </div>
          <div className={styles.description}>
            {coupon?.title}
            <br/><br/>
            {coupon?.description}<br/>
          </div>
          {
            (!expiredButton && coupon?.promotion_url) && (
              <button className={styles.btn} onClick={
                showDoubleCheckConfirm 
              } disabled={expiredButton}>
                <span>前往兌換</span>
                <ArrowSVG />
              </button>
            )
          }
        </div>
      </div>
      <Dialog
        visible={doubleCheckVisible}
        message={
          <div className={styles.message}>
            <h2>即將前往外部網站？</h2>
          </div>
        }
        onCancel={onCancel}
        onConfirm={onDoubleCheckConfirm}
        onCancelButtonClick={onCancelButtonClick}
        cancelable
        cancelText="取消"
        okText="確認"
      />
    </>
  );
};

export default CouponDetail;
